import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const BUSINESS_LABELS = {
    cafeteria: 'Cafetería (POS)',
    lonchera: 'Loncheras Escolares',
    cafe_grano: 'Café en Grano'
};

const METHOD_LABELS = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta' };

// Color y signo de cada tipo de movimiento del saldo
const MOVEMENT_STYLES = {
    deposit: { color: '#15803d', bg: '#dcfce7', icon: 'bi-plus-circle-fill' },
    charge: { color: '#b91c1c', bg: '#fee2e2', icon: 'bi-cart-fill' },
    refund: { color: '#0369a1', bg: '#e0f2fe', icon: 'bi-arrow-counterclockwise' },
    withdrawal: { color: '#b45309', bg: '#fef3c7', icon: 'bi-box-arrow-up-right' }
};

const money = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    background: 'white',
    boxSizing: 'border-box'
};

const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '6px' };

// Qué se compró en una orden pagada con saldo
const describeOrder = (order) => {
    if (!order) return '';
    if (order.business_type === 'lonchera' && order.school_name) {
        return `${order.lunchbox_count} loncheras · ${order.school_name}`;
    }
    const items = (order.items || []).map(it => `${it.quantity}x ${it.product_name}`).join(', ');
    if (order.business_type === 'cafe_grano' && order.coffee_grams) {
        return `${items} (${order.coffee_grams}g)`;
    }
    return items || 'Consumo';
};

/**
 * Abono / saldo a favor de un cliente: registrar dinero adelantado,
 * devolverlo, y ver todo lo que se fue consumiendo con ese saldo.
 */
const SaldoClienteModal = ({ customer, onClose, onChanged }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [type, setType] = useState('deposit');
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('efectivo');
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');

    const loadBalance = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/api/customers/admin/${customer.id}/balance/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setData(res.data.data);
        } catch (err) {
            console.error('Error cargando saldo a favor:', err);
            alert('Error al consultar el saldo: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    }, [customer.id]);

    useEffect(() => {
        loadBalance();
    }, [loadBalance]);

    const balance = data?.balance || 0;
    const amountNum = Math.round((parseFloat(amount) || 0) * 100) / 100;
    const balanceAfter = type === 'deposit' ? balance + amountNum : balance - amountNum;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (amountNum <= 0) {
            alert('Ingresa un monto válido.');
            return;
        }
        if (type === 'withdrawal' && amountNum > balance + 0.009) {
            alert(`No se puede devolver más de lo que tiene a favor (${money(balance)}).`);
            return;
        }
        const action = type === 'deposit' ? 'ABONO' : 'DEVOLUCIÓN';
        if (!window.confirm(`Registrar ${action} de ${money(amountNum)} (${METHOD_LABELS[method]}).\nEl saldo a favor quedará en ${money(balanceAfter)}.\n\n¿Continuar?`)) return;

        setProcessing(true);
        try {
            await api.post(`/api/customers/admin/${customer.id}/balance/`, {
                type,
                amount: amountNum,
                payment_method: method,
                reference,
                notes
            }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setAmount('');
            setReference('');
            setNotes('');
            await loadBalance();
            onChanged && onChanged();
        } catch (err) {
            console.error('Error registrando abono:', err);
            alert('Error al registrar: ' + (err.response?.data?.message || err.message));
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1100, padding: '16px'
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: 'white', borderRadius: '16px', maxWidth: '760px', width: '100%',
                    maxHeight: '92vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }}
            >
                {/* Header */}
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(to right, #f5f3ff, #f8fafc)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className="bi bi-wallet2" style={{ color: '#7c3aed' }}></i> Abono / Saldo a Favor
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                            Cliente: <strong>{customer.first_name} {customer.last_name}</strong> (CI: {customer.cedula || '9999999999999'})
                        </p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>×</button>
                </div>

                <div style={{ padding: '24px' }}>
                    {loading && !data ? (
                        <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748b', fontSize: '14px' }}>
                            Consultando saldo a favor...
                        </div>
                    ) : (
                        <>
                            {/* Resumen */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                                <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '14px 16px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#6d28d9', textTransform: 'uppercase' }}>Saldo disponible</div>
                                    <div style={{ fontSize: '26px', fontWeight: '900', color: '#6d28d9' }}>{money(balance)}</div>
                                </div>
                                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '14px 16px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#166534', textTransform: 'uppercase' }}>Total abonado</div>
                                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#15803d' }}>{money(data?.total_deposited)}</div>
                                </div>
                                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '14px 16px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#991b1b', textTransform: 'uppercase' }}>Total consumido</div>
                                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#b91c1c' }}>{money(data?.total_consumed)}</div>
                                </div>
                            </div>

                            {/* Formulario de abono / devolución */}
                            <form onSubmit={handleSubmit} style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                                    {[
                                        { id: 'deposit', label: 'Registrar abono', icon: 'bi-plus-circle' },
                                        { id: 'withdrawal', label: 'Devolver saldo', icon: 'bi-box-arrow-up-right' }
                                    ].map(opt => (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            onClick={() => setType(opt.id)}
                                            disabled={opt.id === 'withdrawal' && balance <= 0}
                                            style={{
                                                flex: 1, padding: '8px 10px', borderRadius: '8px', fontSize: '13px', fontWeight: '700',
                                                border: `1px solid ${type === opt.id ? '#7c3aed' : '#cbd5e1'}`,
                                                background: type === opt.id ? '#ede9fe' : 'white',
                                                color: type === opt.id ? '#6d28d9' : '#475569',
                                                cursor: opt.id === 'withdrawal' && balance <= 0 ? 'not-allowed' : 'pointer',
                                                opacity: opt.id === 'withdrawal' && balance <= 0 ? 0.5 : 1
                                            }}
                                        >
                                            <i className={`bi ${opt.icon}`}></i> {opt.label}
                                        </button>
                                    ))}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '14px' }}>
                                    <div>
                                        <label style={labelStyle}>{type === 'deposit' ? 'Monto que abona ($ USD) *' : 'Monto a devolver ($ USD) *'}</label>
                                        <input
                                            type="number" step="0.01" min="0.01" required
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder="0.00"
                                            style={{ ...inputStyle, fontSize: '15px', fontWeight: '700' }}
                                        />
                                        {amountNum > 0 && (
                                            <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: '700', color: balanceAfter < -0.009 ? '#dc2626' : '#6d28d9' }}>
                                                {balanceAfter < -0.009 ? `Solo tiene ${money(balance)} a favor` : `Saldo quedará en ${money(balanceAfter)}`}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label style={labelStyle}>{type === 'deposit' ? 'Forma de pago recibida *' : 'Forma de devolución *'}</label>
                                        <select value={method} onChange={(e) => setMethod(e.target.value)} style={inputStyle}>
                                            <option value="efectivo">Efectivo</option>
                                            <option value="transferencia">Transferencia / Banco / DeUna</option>
                                            <option value="tarjeta">Tarjeta Débito / Crédito</option>
                                        </select>
                                    </div>
                                </div>

                                {method === 'transferencia' && (
                                    <div style={{ marginBottom: '14px' }}>
                                        <label style={labelStyle}>Número de referencia / comprobante</label>
                                        <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ej: 987654321" style={inputStyle} />
                                    </div>
                                )}

                                <div style={{ marginBottom: '14px' }}>
                                    <label style={labelStyle}>Notas (opcional)</label>
                                    <input
                                        type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                                        placeholder={type === 'deposit' ? 'Ej: Abono del mes de noviembre' : 'Ej: Se retira del plan'}
                                        style={inputStyle}
                                    />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        type="submit"
                                        disabled={processing}
                                        style={{
                                            padding: '10px 22px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '8px',
                                            fontSize: '14px', fontWeight: '700', cursor: processing ? 'not-allowed' : 'pointer',
                                            display: 'inline-flex', alignItems: 'center', gap: '8px'
                                        }}
                                    >
                                        <i className="bi bi-check2-circle" style={{ fontSize: '18px' }}></i>
                                        {processing ? 'Registrando...' : (type === 'deposit' ? 'Confirmar Abono' : 'Confirmar Devolución')}
                                    </button>
                                </div>
                            </form>

                            {/* Historial */}
                            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px 0' }}>
                                Historial de abonos y consumos
                            </h4>
                            {data?.movements?.length ? (
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflowX: 'auto', maxHeight: '340px', overflowY: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                        <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0 }}>
                                            <tr>
                                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Fecha</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Movimiento</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Detalle</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Monto</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Saldo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.movements.map(m => {
                                                const st = MOVEMENT_STYLES[m.type] || MOVEMENT_STYLES.deposit;
                                                return (
                                                    <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                                                        <td style={{ padding: '8px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                                            {new Date(m.created_at).toLocaleString('es-EC', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                                            <span style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                <i className={`bi ${st.icon}`}></i> {m.type_label}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', color: '#334155' }}>
                                                            {m.order ? (
                                                                <>
                                                                    <div style={{ fontWeight: '700', color: '#0f172a' }}>
                                                                        #{m.order.order_number} · {BUSINESS_LABELS[m.order.business_type] || m.order.business_type}
                                                                    </div>
                                                                    <div>{describeOrder(m.order)}</div>
                                                                    {m.order.balance_amount < m.order.total - 0.009 && (
                                                                        <div style={{ color: '#64748b' }}>Total {money(m.order.total)} (resto con otra forma de pago)</div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {m.method && <div>{METHOD_LABELS[m.method] || m.method}{m.reference ? ` · Ref: ${m.reference}` : ''}</div>}
                                                                    {m.notes && <div style={{ color: '#64748b' }}>{m.notes}</div>}
                                                                </>
                                                            )}
                                                            {m.created_by_name && <div style={{ color: '#94a3b8', fontSize: '11px' }}>por {m.created_by_name}</div>}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '800', color: m.amount >= 0 ? '#15803d' : '#b91c1c', whiteSpace: 'nowrap' }}>
                                                            {m.amount >= 0 ? '+' : '-'}{money(Math.abs(m.amount))}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700', color: '#6d28d9', whiteSpace: 'nowrap' }}>
                                                            {money(m.balance_after)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                                    Este cliente todavía no tiene abonos registrados.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SaldoClienteModal;
